import { isRemoteReceiver, remoteMangaExtensions } from '../cast/RemoteSession.ts';
/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import type {
    YomiriChapter,
    YomiriExtension,
    YomiriManga,
    YomiriMangasPage,
    YomiriPage,
} from '@/yomiri/types/extension.ts';

import { declarativeSources, restoreRuntimeSources, installRuntimeSource, uninstallRuntimeRepository } from '@/yomiri/source-engine/RuntimeSources.ts';
import type { RepositoryEntry } from '../repositories/RepositoryStore.ts';
import { AutoWebSource } from '@/yomiri/sources/AutoWebSource.ts';
import { mangaPlusSource } from '@/yomiri/sources/MangaPlusSource.ts';
import { localMangaChapters, localChapterPages } from '@/yomiri/storage/LocalChapters.ts';
import { yomiriDatabase } from '@/yomiri/storage/YomiriDatabase.ts';
import { getSourceCacheScope } from '@/yomiri/core/SourceSession.ts';
import { normalizeSourceId } from '@/yomiri/network/SourceRegistry.ts';
import { EnchiladaSource, OlympusSource, InMangaSource, NovelCoolSource } from '../sources/MaintainedSources.ts';
import { sourceHttpClient } from './SourceHttp.ts';
import { bridgedTestMangaSourceInstance } from '@/yomiri/sources/BridgedTestMangaSource.ts';
import { storedRepositoryEntries } from '../repositories/RepositoryStore.ts';

/**
 * Central registry and dispatcher for all manga sources in Yomiri.
 * The UI communicates solely with SourceProvider, unaware of the underlying
 * data provider (MockSource, in-browser extension, external API, etc.).
 */
export class SourceProvider {
    private static instance: SourceProvider;

    private extensions: Map<string, YomiriExtension> = new Map();
    private sessionCache = new Map<string, { expires: number; value: Promise<any> }>();
    private cached<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
        key = `${getSourceCacheScope()}:${key}`;
        const existing = this.sessionCache.get(key);
        if (existing && existing.expires > Date.now()) return existing.value.then((value) => structuredClone(value));
        const value = fetcher().catch((error) => { if (this.sessionCache.get(key)?.value === value) this.sessionCache.delete(key); throw error; });
        this.sessionCache.set(key, { expires: Date.now() + 10 * 60 * 1000, value });
        if (this.sessionCache.size > 250) this.sessionCache.delete(this.sessionCache.keys().next().value!);
        return value.then((result) => structuredClone(result));
    }

    private disabledSourceIds: Set<string> = new Set();
    public clearResponseCache(): void {
        this.sessionCache.clear();
        this.extensions.forEach(extension => extension.clearSessionCache?.());
    }
    private installedSourceIds = new Set<string>();

    private constructor() {
        this.initDefaultSources();
        if (typeof window !== 'undefined') window.addEventListener('yomiri-transport-changed', () => { this.sessionCache.clear(); this.extensions.forEach(extension => extension.clearSessionCache?.()); });
        try {
            const hasLocalStorage = typeof localStorage !== 'undefined';
            const saved = hasLocalStorage ? JSON.parse(localStorage.getItem('yomiri_disabled_sources') || '[]') : [];
            if (Array.isArray(saved)) {
                this.disabledSourceIds = new Set(saved.filter((id): id is string => typeof id === 'string' && this.extensions.has(id)));
            }
            const added = hasLocalStorage ? (JSON.parse(localStorage.getItem('yomiri_maintained_installed') || '[]') as string[]) : [];
            for (const id of ['enchiladascan','olympusscanlation','inmanga','novelcool'])
                if (!added.includes(id)) this.disabledSourceIds.add(id);

            const stored = hasLocalStorage ? localStorage.getItem('yomiri_installed_sources_v2') : null;
            const available = new Set(this.getAvailableSourceIds());
            if (stored === null || (available.size === 0 && typeof window === 'undefined')) {
                // If not configured yet or running in node/test environment, default sources are installed
                const defaultSources = ['mangadex', 'leercapitulo', 'kazokuden', 'anzmanga', 'mangaplus', 'bokugen', 'dbnewhope', 'marmota'];
                this.installedSourceIds = new Set(defaultSources);
            } else if (available.size === 0) {
                // If there are no configured repositories, no sources can be considered installed
                this.installedSourceIds = new Set();
            } else {
                const ids = stored === null ? [] : JSON.parse(stored);
                this.installedSourceIds = new Set(
                    Array.isArray(ids)
                        ? ids.filter((id): id is string => typeof id === 'string' && available.has(id))
                        : []
                );
            }
            if (hasLocalStorage && stored !== null) {
                localStorage.setItem('yomiri_installed_sources_v2', JSON.stringify([...this.installedSourceIds]));
            }
        } catch {
            this.disabledSourceIds = new Set();
            this.installedSourceIds = new Set();
        }

    }

    private initDefaultSources(): void {
        declarativeSources.forEach(source=>this.registerExtension(source));
        [EnchiladaSource, OlympusSource, InMangaSource, NovelCoolSource].forEach(Source=>this.registerExtension(new Source(sourceHttpClient)));
        restoreRuntimeSources().forEach(source=>this.registerExtension(source));
        this.registerExtension(mangaPlusSource);
        this.registerExtension(bridgedTestMangaSourceInstance);
        this.registerExtension(new AutoWebSource({ id: 'bokugen', name: 'BokugenTranslation', baseUrl: 'https://bokugents.com', family: 'MangaThemesia', packageName: 'yomiri.bokugen', iconUrl: '/source-icons/bokugen.png' }));
    }

    public static getInstance(): SourceProvider {
        if (!SourceProvider.instance) {
            SourceProvider.instance = new SourceProvider();
        }
        return SourceProvider.instance;
    }

    /**
     * Register a new extension/source runtime
     */
    public registerExtension(extension: YomiriExtension): void {
        this.extensions.set(extension.id, extension);
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('yomiri-sources-changed'));
    }
    public async installRepositorySource(entry:RepositoryEntry):Promise<void>{
        if(isRemoteReceiver())throw new Error('Instalá esta fuente desde el móvil vinculado.');
        const source=await installRuntimeSource(entry,id=>this.getExtension(id));this.registerExtension(source);this.setSourceEnabled(source.id,true);
    }
    public removeRuntimeRepository(repository:string):void{uninstallRuntimeRepository(repository).forEach(id=>this.unregisterExtension(id));this.sessionCache.clear();}

    /**
     * Unregister an extension
     */
    public unregisterExtension(extensionId: string): void {
        this.extensions.delete(extensionId);
    }

    /**
     * Get all registered extensions
     */
    public getAllExtensions(): YomiriExtension[] {
        if (isRemoteReceiver()) return remoteMangaExtensions();
        return Array.from(this.extensions.values());
    }

    /**
     * Get only enabled extensions
     */
    public getEnabledExtensions(): YomiriExtension[] {
        if (isRemoteReceiver()) return remoteMangaExtensions();
        const available = new Set(this.getAvailableSourceIds());
        return Array.from(this.extensions.values()).filter(
            (ext) => available.has(ext.id) && this.installedSourceIds.has(ext.id) && !this.disabledSourceIds.has(ext.id)
        );
    }
    public getAvailableSourceIds(): string[] {
        if (isRemoteReceiver()) return remoteMangaExtensions().map(source=>source.id);
        try {
            const repositories: string[] = JSON.parse(localStorage.getItem('yomiri_repositories') || '[]');
            if (repositories.length === 0) return [];
            const catalogs = JSON.parse(localStorage.getItem('yomiri_repository_catalogs') || '{}');
            const entries = storedRepositoryEntries();
            return [...new Set(repositories.flatMap((url) => ['/yomiri-sources.json','/repositories/yomiri/index.json'].some(path=>url===path||url===new URL(path,location.origin).href) ? ['mangadex', 'leercapitulo', 'kazokuden', 'anzmanga', 'mangaplus', 'bokugen', 'dbnewhope', 'marmota'] : entries[url]?.map(e=>e.runtimeId) || catalogs[url] || []))] as string[];
        } catch { return []; }
    }

    /**
     * Find an extension by its ID
     */
    public getExtension(extensionId: string): YomiriExtension | undefined {
        if (isRemoteReceiver()) return remoteMangaExtensions().find(source=>source.id===extensionId);
        return this.extensions.get(normalizeSourceId(extensionId));
    }

    /**
     * Checks if a source is enabled
     */
    public isSourceEnabled(sourceId: string): boolean {
        if (isRemoteReceiver()) return remoteMangaExtensions().some(source=>source.id===sourceId);
        return this.installedSourceIds.has(sourceId) && !this.disabledSourceIds.has(sourceId);
    }

    /**
     * Enables or disables a source
     */
    public setSourceEnabled(sourceId: string, enabled: boolean): void {
        if(isRemoteReceiver())throw new Error('Administrá esta fuente desde el móvil vinculado.');
        if (enabled) {
            this.installedSourceIds.add(sourceId);
            this.disabledSourceIds.delete(sourceId);
        } else {
            this.disabledSourceIds.add(sourceId);
        }
        try {
            if (enabled && ['enchiladascan','olympusscanlation','inmanga','novelcool'].includes(sourceId)) {
                const added = JSON.parse(localStorage.getItem('yomiri_maintained_installed') || '[]') as string[];
                localStorage.setItem('yomiri_maintained_installed', JSON.stringify([...new Set([...added,sourceId])]));
            }
            localStorage.setItem('yomiri_installed_sources_v2', JSON.stringify([...this.installedSourceIds]));
            localStorage.setItem('yomiri_disabled_sources', JSON.stringify([...this.disabledSourceIds]));
        } catch {
            // Source selection still works for this session when storage is unavailable.
        }
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('yomiri-sources-changed'));
    }

    public isSourceInstalled(id: string): boolean { return isRemoteReceiver() ? this.isSourceEnabled(id) : this.installedSourceIds.has(id); }
    public getInstalledExtensions(): YomiriExtension[] { return isRemoteReceiver()?this.getAllExtensions():this.getAllExtensions().filter(source => this.installedSourceIds.has(source.id)); }
    public uninstallSource(id: string): void {
        if(isRemoteReceiver())throw new Error('Desinstalá esta fuente desde el móvil vinculado.');
        this.installedSourceIds.delete(id);
        this.disabledSourceIds.add(id);
        localStorage.setItem('yomiri_installed_sources_v2', JSON.stringify([...this.installedSourceIds]));
        localStorage.setItem('yomiri_disabled_sources', JSON.stringify([...this.disabledSourceIds]));
        this.sessionCache.clear();
        window.dispatchEvent(new Event('yomiri-sources-changed'));
    }

    /**
     * Runs health check on a source
     */
    public async checkSourceHealth(sourceId: string): Promise<{ ok: boolean; message: string }> {
        const ext = this.getRequiredExtension(sourceId);
        if (ext.checkHealth) {
            return ext.checkHealth();
        }
        return { ok: true, message: 'Fuente activa (sin endpoint específico de diagnóstico).' };
    }

    /**
     * Search manga using a specific source
     */
    public async search(
        sourceId: string,
        query: string,
        page: number = 1,
        filters?: Record<string, any>,
    ): Promise<YomiriMangasPage> {
        const ext = this.getRequiredExtension(sourceId);
        return this.cached(JSON.stringify(['search', sourceId, query, page, filters]), () => ext.search(query, page, filters));
    }

    /**
     * Get popular manga from a source
     */
    public async getPopular(sourceId: string, page: number = 1): Promise<YomiriMangasPage> {
        const ext = this.getRequiredExtension(sourceId);
        return this.cached(`popular:${sourceId}:${page}`, () => ext.getPopular(page));
    }

    /**
     * Get latest manga from a source
     */
    public async getLatest(sourceId: string, page: number = 1): Promise<YomiriMangasPage> {
        const ext = this.getRequiredExtension(sourceId);
        if (ext.getLatest) {
            const languages = sourceId === 'mangadex' ? await yomiriDatabase.getSetting<string[]>('source_languages_mangadex', ['es']) : [];
            return this.cached(`latest:${sourceId}:${page}:${JSON.stringify(languages)}`, () => ext.getLatest!(page));
        }
        return this.getPopular(sourceId, page);
    }

    /**
     * Get full manga details
     */
    public async getManga(sourceId: string, mangaId: string): Promise<YomiriManga> {
        if (isRemoteReceiver()) return this.getRequiredExtension(sourceId).getManga(mangaId);
        const local = (await localMangaChapters(sourceId, mangaId))[0];
        if (local && (!navigator.onLine || !this.getAvailableSourceIds().includes(sourceId))) return local.manga;
        const ext = this.getRequiredExtension(sourceId);
        return this.cached(`manga:${sourceId}:${mangaId}`, () => ext.getManga(mangaId)).catch(error => { if (local) return local.manga; throw error; });
    }

    /**
     * Get all chapters for a manga
     */
    public async getChapters(sourceId: string, mangaId: string, options?: { language?: string; languages?: string[] }): Promise<YomiriChapter[]> {
        if (isRemoteReceiver()) return this.getRequiredExtension(sourceId).getChapters(mangaId,options);
        const local = (await localMangaChapters(sourceId, mangaId)).map(r => r.chapter);
        if (local.length && (!navigator.onLine || !this.getAvailableSourceIds().includes(sourceId))) return local.sort((a,b) => b.chapterNumber - a.chapterNumber);
        const ext = this.getRequiredExtension(sourceId);
        return this.cached(JSON.stringify(['chapters', sourceId, mangaId, options]), () => ext.getChapters(mangaId, options)).then(remote => [...new Map([...local, ...remote].map(chapter => [chapter.id, chapter])).values()].sort((a, b) => b.chapterNumber - a.chapterNumber)).catch(error => { if (local.length) return local; throw error; });
    }

    /**
     * Get page URLs for a chapter
     */
    public async getPages(sourceId: string, chapterId: string): Promise<YomiriPage[]> {
        if (isRemoteReceiver()) return this.getRequiredExtension(sourceId).getPages(chapterId);
        const local = await localChapterPages(sourceId, chapterId);
        if (local) return local;
        const ext = this.getRequiredExtension(sourceId);
        return this.cached(`pages:${sourceId}:${chapterId}`, () => ext.getPages(chapterId)).then(pages => pages.map(page => ({ ...page, sourceId: ext.id })));
    }

    private getRequiredExtension(sourceId: string): YomiriExtension {
        const ext = this.getExtension(sourceId);
        if (!ext || !this.getAvailableSourceIds().includes(ext.id) || !this.isSourceInstalled(ext.id)) throw new Error('Instalá esta fuente desde Fuentes → Extensiones y repositorios para abrir su catálogo y capítulos.');
        if (!ext) {
            throw new Error(`Extension/Source with id "${sourceId}" not found in SourceProvider.`);
        }
        return ext;
    }
}

export const sourceProvider = SourceProvider.getInstance();
